import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

type Args = {
  bankAccountIds: string[];
  dateFrom: string; // YYYY-MM-DD
  dateTo: string;   // YYYY-MM-DD
  enabled?: boolean;
};

export type TxInOutDayRow = {
  tx_count: number;
  in_count: number;
  out_count: number;
  in_amount: number;
  out_amount: number;
  fee_total: number;
  currency: string | null;
};

export type TxInOutMatrix = {
  dateKeys: string[];
  byAccount: Map<string, Map<string, TxInOutDayRow>>;
};

const toNum = (v: unknown): number => {
  if (v == null) return 0;
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const pad2 = (n: number) => String(n).padStart(2, "0");

function buildDateKeys(dateFrom: string, dateTo: string): string[] {
  const start = new Date(`${dateFrom}T00:00:00Z`);
  const end = new Date(`${dateTo}T00:00:00Z`);
  const keys: string[] = [];

  for (let d = new Date(start); d <= end; d.setUTCDate(d.getUTCDate() + 1)) {
    keys.push(`${d.getUTCFullYear()}-${pad2(d.getUTCMonth() + 1)}-${pad2(d.getUTCDate())}`);
  }

  return keys;
}

export function useTxInOutMatrixRange(args: Args) {
  const { bankAccountIds, dateFrom, dateTo, enabled = true } = args;

  return useQuery({
    queryKey: ["tx_in_out_matrix_rpc", bankAccountIds, dateFrom, dateTo],
    enabled: enabled && bankAccountIds.length > 0 && !!dateFrom && !!dateTo,
    queryFn: async (): Promise<TxInOutMatrix> => {
      const dateKeys = buildDateKeys(dateFrom, dateTo);

      const tsFrom = `${dateFrom}T00:00:00+00:00`;
      const toPlus1 = new Date(`${dateTo}T00:00:00Z`);
      toPlus1.setUTCDate(toPlus1.getUTCDate() + 1);
      const tsToPlus1 = toPlus1.toISOString();

      const { data, error } = await (supabase as any).rpc("get_bank_tx_daily_in_out", {
        p_account_ids: bankAccountIds,
        p_from: tsFrom,
        p_to: tsToPlus1,
      });
      if (error) throw error;

      const byAccount = new Map<string, Map<string, TxInOutDayRow>>();
      for (const id of bankAccountIds) {
        byAccount.set(id, new Map());
      }

      for (const row of (data ?? []) as any[]) {
        const accId = String(row.bank_account_id);
        const dayKey = String(row.day).slice(0, 10);

        const accMap = byAccount.get(accId) ?? new Map<string, TxInOutDayRow>();
        accMap.set(dayKey, {
          tx_count: toNum(row.tx_count),
          in_count: toNum(row.in_count),
          out_count: toNum(row.out_count),
          in_amount: toNum(row.in_amount),
          out_amount: toNum(row.out_amount),
          fee_total: toNum(row.fee_total),
          currency: row.currency ?? null,
        });
        byAccount.set(accId, accMap);
      }

      return { dateKeys, byAccount };
    },
    staleTime: 15_000,
  });
}
